import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  billingElectionRecord,
  captureQualification,
  clinicalNoteAddendum,
  clinicalNoteAttest,
  clinicalNoteGenerate,
  clinicalNoteReturn,
  clinicalNotes,
  residentCareTime,
  type ApiError,
  type ClinicalNote,
  type ClinicalNotesResponse,
  type CaptureQualification,
  type ElectionDisclosure,
} from '@aithora/shared';
import { ConfirmDialog, withConfirm } from '../../ui/ConfirmDialog';
import { sx } from '../../ui/designStyles';
import { EmptyState, ErrorState, Skeleton } from '../../ui/states';
import { type ResidentsCtx, useApi } from './common';
import { notesVals, type NotesUi } from './notesVals';
import { NotesView } from './NotesView';
import { TeamAccessRequest, isTeamRestricted } from './TeamAccess';

/** The five things CMS requires the resident be told for a valid CCM / BHI election (MLN909188). */
export const DISCLOSURES: Array<[ElectionDisclosure, string]> = [
  ['service_availability', 'Their care team can help them between visits, and they can get this help.'],
  ['one_biller_per_month', 'Only one practitioner can bill this service each month.'],
  ['cost_sharing', 'They may owe cost sharing (the Part B coinsurance).'],
  ['right_to_stop', 'They can stop at any time, effective at the end of the month.'],
  ['explained_and_accepted', 'Someone went over these points with them, and they said yes.'],
];

/** Clinical Notes (design rIsNotes, T3646): Facility Admin and Aithora only. */
export function NotesTab({ ctx }: { ctx: ResidentsCtx }) {
  const navigate = useNavigate();
  const q = useApi<ClinicalNotesResponse>((c, signal) => clinicalNotes(c, { query: { organizationId: ctx.orgId ?? undefined }, signal }));
  // Live qualification for this building's month (the threshold and what each claim still
  // needs). Optional: the notes work without it, and a reader without the books gets a 403.
  const qual = useApi<CaptureQualification | null>((c, signal) =>
    captureQualification(c, { query: { facility: ctx.orgId ?? undefined }, signal }).catch(() => null), [ctx.orgId], { enabled: !!ctx.orgId });
  const [ui, setUiState] = useState<NotesUi>({ sel: null, filter: null, open: {}, allOpen: false, edits: {}, editing: null, backFor: null, backWhy: '', composing: null, logged: {} });
  const [err, setErr] = useState<ApiError | null>(null);
  // The election dialog (restored from production): the note it unblocks, and what was told.
  const [elect, setElect] = useState<ClinicalNote | null>(null);
  const [told, setTold] = useState<Record<ElectionDisclosure, boolean>>({ service_availability: false, one_biller_per_month: false, cost_sharing: false, right_to_stop: false, explained_and_accepted: false });
  const [start] = useState(() => Date.now());
  const [tick, setTick] = useState(0);
  const setUi = (p: Partial<NotesUi>) => setUiState((s) => ({ ...s, ...p }));
  // The queue's waiting clocks run (design: one page ticker, once a second).
  useEffect(() => {
    const id = window.setInterval(() => setTick(Math.round((Date.now() - start) / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [start]);

  if (q.loading && !q.data && !q.error) return <Skeleton rows={6} label="Loading clinical notes" />;
  if (!q.data && isTeamRestricted(q.error)) return <TeamAccessRequest client={ctx.client} orgId={ctx.orgId ?? null} residentId={null} onSent={q.reload} />;
  if (!q.data) return q.error ? <ErrorState error={q.error} onRetry={q.reload} /> : null;
  if (!q.data.notes.length) {
    return <EmptyState title="Nothing is waiting on you." body="No clinical note drafts this month. A draft appears here once a month of Atlas care is composed into one." />;
  }
  const fail = (e: ApiError) => setErr(e);
  const org = ctx.orgId ?? undefined;
  const sign = (n: ClinicalNote, edits?: Record<string, string>) =>
    clinicalNoteAttest(ctx.client, { params: { id: n.resident_id }, body: { noteId: n.id, organizationId: org, edits: edits && Object.keys(edits).length ? edits : undefined } });
  const regenerate = (n: ClinicalNote) => {
    setErr(null);
    setUi({ composing: n.id });
    clinicalNoteGenerate(ctx.client, { params: { id: n.resident_id }, body: { organizationId: org, period: n.period, cptCode: n.cpt_code } })
      .then(() => q.reload())
      .catch(fail)
      .finally(() => setUi({ composing: null }));
  };
  const V = notesVals({
    data: q.data,
    ui,
    setUi,
    canWrite: ctx.canAct,
    roleLabel: ctx.roleLabel,
    elapsedSec: tick,
    go: (path) => navigate(path),
    generate: regenerate,
    qualFor: (rid, code) => qual.data?.residents.find((r) => r.residentId === rid)?.rows.find((x) => x.code === code),
    recordElection: (n) => {
      setTold({ service_availability: false, one_biller_per_month: false, cost_sharing: false, right_to_stop: false, explained_and_accepted: false });
      setElect(n);
    },
    sign: (n, edits) => { setErr(null); sign(n, edits).then(() => q.reload()).catch(fail); },
    signMany: (ns) => { setErr(null); Promise.all(ns.map((n) => sign(n))).then(() => q.reload()).catch(fail); },
    sendBack: (n, reason) => { setErr(null); clinicalNoteReturn(ctx.client, { params: { id: n.id }, body: { reason } }).then(() => q.reload()).catch(fail); },
    addendum: (n) => { setErr(null); clinicalNoteAddendum(ctx.client, { params: { id: n.id }, body: {} }).then((r) => { q.reload(); setUi({ sel: r.id }); }).catch(fail); },
    logMinutes: (n, minutes) => {
      setErr(null);
      residentCareTime(ctx.client, { params: { id: n.resident_id }, body: { minutes } })
        .then(() => setUi({ logged: { ...ui.logged, [n.id]: (ui.logged[n.id] || 0) + minutes } }))
        .catch(fail);
    },
  });
  return (
    <>
      <NotesView V={{ ...V, rIsNotes: true }} />
      {err && <ErrorState error={err} />}
      {elect && (
        <ConfirmDialog
          title={'Record the ' + (elect.cpt_code === '99484' ? 'BHI' : 'CCM') + ' election for ' + (elect.resident_name ?? 'this resident')}
          confirmLabel="Record election"
          reason={{ label: 'Witnessed by', placeholder: 'Who heard the resident agree', required: false, showLabel: true }}
          body={
            <div style={sx('display:flex;flex-direction:column;gap:7px;')}>
              <span>The resident (or their representative) agreed, after being told all five:</span>
              {DISCLOSURES.map(([k, text]) => (
                <label key={k} style={sx('display:flex;align-items:flex-start;gap:8px;font-size:16px;color:#22272E;line-height:1.4;')}>
                  <input type="checkbox" checked={told[k]} onChange={(e) => setTold((t) => ({ ...t, [k]: e.target.checked }))} />
                  <span>{text}</span>
                </label>
              ))}
              <span style={sx('font-size:14px;color:#54606C;')}>The draft is recomposed after, so the gate reads the election.</span>
            </div>
          }
          onConfirm={async (witness) => {
            await billingElectionRecord(ctx.client, {
              params: { id: elect.resident_id },
              body: withConfirm({ consentType: elect.cpt_code === '99484' ? 'bhi_election' : 'ccm_election', disclosures: told, witnessedBy: witness || undefined }),
            });
            regenerate(elect);
          }}
          onClose={() => setElect(null)}
        />
      )}
    </>
  );
}
